import React, { PureComponent } from "react";
import client from "@culturehq/client";
import { Panel, Subnav } from "@culturehq/components";

import { useProfile } from "app/context";
import { Trans } from "app/utils";
import getBadgeTypes from "constants/getBadgeTypes";

const BADGE_CATEGORIES = [
  { value: null, label: "View All" },
  { value: "general", label: "General" },
  { value: "invite", label: "New People" },
  { value: "stories", label: "Stories" }
];

const getBadgeCategories = ({
  organization: { employerBranding, eventsEnabled, recognitionsEnabled }
}) => {
  const categories = JSON.parse(JSON.stringify(BADGE_CATEGORIES));
  if (eventsEnabled) {
    categories.push({ value: "events", label: "Events" });
  }
  if (recognitionsEnabled) {
    categories.push({ value: "recognition", label: "Recognition" });
  }
  if (employerBranding) {
    categories.push({ value: "social", label: "Social" });
  }
  return categories;
};

const BadgeType = ({ badgeType, breakdown }) => {
  const { image, description, points, camel } = badgeType;
  const times = ((breakdown || {})[camel] || { count: 0 }).count;

  return (
    <div className="row chart-row badge-row">
      <div className="col-xs-3">
        <div className="Badge">
          <img src={image} alt={description} />
          <span>+{points}</span>
        </div>
      </div>
      <div className="col-xs-7 description">
        <p>{description}</p>
      </div>
      <div className="col-xs-2 count">
        {times > 0 && <p className="text-right">x {times}</p>}
      </div>
    </div>
  );
};

class PointsContext extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      badgeTypes: getBadgeTypes(props.profile),
      badgeCategories: getBadgeCategories(props.profile),
      breakdown: null
    };
  }

  componentDidMount() {
    this.fetchCounts();
  }

  componentDidUpdate(prevProps) {
    const { profile: { id: profileId } } = this.props;

    if (prevProps.profile.id !== profileId) {
      this.fetchCounts();
    }
  }

  getBadgeTypesByIndex = index => {
    const { profile } = this.props;
    const { badgeCategories } = this.state;

    const category = badgeCategories[index].value;
    const badgeTypes = getBadgeTypes(profile);

    if (!category) {
      return badgeTypes;
    }

    return badgeTypes.filter(({ category: badgeCategory }) => badgeCategory === category);
  };

  fetchCounts = async () => {
    const { profile: { id: userId } } = this.props;
    const { pointBreakdown: { breakdown } } = await client.getUserPointBreakdown({ userId });

    this.setState({ breakdown });
  };

  handleCategorySelected = index => {
    this.setState({ badgeTypes: this.getBadgeTypesByIndex(index) });
  };

  render() {
    const { badgeTypes, badgeCategories, breakdown } = this.state;

    return (
      <Panel className="Points" limitWidth>
        <Panel.Heading primary>
          <Trans>Community Score</Trans>
        </Panel.Heading>
        <Panel.Body>
          <div className="clearfix">
            <div className="col-sm-12">
              <h3><Trans>What is a Community Score? How do I improve mine?</Trans></h3>
              <p className="description"><Trans>Your Community Score is a measure of how much you are contributing towards a better community at your organization. Your total score is based on the specific actions below, while your badges represent the diversity of your contribution. The more you contribute, the higher your score, the more you help strengthen your workplace community. Have some fun, meet new people, and take part in transforming your organization’s culture.</Trans></p>
            </div>

            <div className="col-sm-12">
              <Subnav onChange={this.handleCategorySelected}>
                {badgeCategories.map(category => (
                  <Subnav.Item key={category.label}>{category.label}</Subnav.Item>
                ))}
              </Subnav>
            </div>

            <div className="col-sm-12 chart">
              <div className="row chart-header">
                <div className="col-xs-3"><Trans>Earned</Trans></div>
                <div className="col-xs-7"><Trans>Action</Trans></div>
                <div className="col-xs-2 text-right"><Trans>Count</Trans></div>
              </div>

              {badgeTypes.map((badgeType, index) => (
                <BadgeType
                  key={index}
                  badgeType={badgeType}
                  breakdown={breakdown}
                />
              ))}
            </div>
          </div>
        </Panel.Body>
      </Panel>
    );
  }
}

const Points = () => {
  const { profile } = useProfile();

  return <PointsContext profile={profile} />;
};

export default Points;
